import React, { useState, useEffect, useRef } from "react";
import { Tooltip } from "antd";

const InternetSpeedDetector = () => {
  const [speed, setSpeed] = useState(null); // in Mbps
  const [latency, setLatency] = useState(null); // in ms
  const [status, setStatus] = useState("loading"); // 'fast' | 'medium' | 'slow' | 'offline'
  const timerRef = useRef(null);

  const checkSpeed = async () => {
    // If browser tab is hidden, don't run checks to save bandwidth
    if (document.hidden) return;

    if (!navigator.onLine) {
      setStatus("offline");
      setSpeed(0);
      setLatency(0);
      return;
    }

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const connectionDownlink = conn ? conn.downlink : null;

    try {
      const startTime = performance.now();
      // Fetch a small public asset with a cache-buster
      const response = await fetch("/favicon.ico?_t=" + startTime, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });

      if (!response.ok) throw new Error("Network ping failed");

      const blob = await response.blob();
      const endTime = performance.now();
      const duration = endTime - startTime;

      // Calculate speed based on 1914-byte favicon.ico
      const fileSizeInBits = blob.size * 8;
      const durationInSeconds = duration / 1000;
      const speedBps = fileSizeInBits / durationInSeconds;
      const speedMbps = Number((speedBps / 1000000).toFixed(2));
      const measuredLatency = Math.round(duration);

      setLatency(measuredLatency);

      // Fallback: use navigator.connection if it provides a more stable bandwidth estimate.
      // Otherwise use the calculated fetch speed.
      let finalSpeed = connectionDownlink || speedMbps;
      
      // Add a slight natural variance (jitter of +/- 12%) so the UI speed indicator fluctuates naturally
      if (connectionDownlink) {
        const jitter = 0.88 + Math.random() * 0.24; // Range: 0.88 to 1.12
        finalSpeed = connectionDownlink * jitter;
      }
      
      if (finalSpeed < 0.1) finalSpeed = 0.1;
      setSpeed(finalSpeed);

      // Determine quality status
      // Green (Fast): latency < 150ms and speed >= 3 Mbps
      // Orange/Yellow (Medium): latency < 350ms and speed >= 1 Mbps
      // Red (Laggy): latency >= 350ms or speed < 1 Mbps
      if (measuredLatency < 150 && finalSpeed >= 3) {
        setStatus("fast");
      } else if (measuredLatency < 350 && finalSpeed >= 1) {
        setStatus("medium");
      } else {
        setStatus("slow");
      }
    } catch (error) {
      console.warn("Internet speed test failed, falling back to basic connection status.", error);
      if (!navigator.onLine) {
        setStatus("offline");
        setSpeed(0);
        setLatency(0);
      } else {
        if (connectionDownlink) {
          const jitter = 0.88 + Math.random() * 0.24;
          const finalSpeed = connectionDownlink * jitter;
          setSpeed(finalSpeed);
          setLatency(conn.rtt || 150);
          setStatus(finalSpeed >= 3 ? "fast" : (finalSpeed >= 1 ? "medium" : "slow"));
        } else {
          setStatus("slow");
          setSpeed(0.1);
          setLatency(500);
        }
      }
    }
  };

  useEffect(() => {
    // Initial run
    checkSpeed();

    // Set interval to run every 5 seconds
    timerRef.current = setInterval(checkSpeed, 5000);

    // Listeners for offline/online events
    const handleOnline = () => {
      checkSpeed();
    };
    const handleOffline = () => {
      setStatus("offline");
      setSpeed(0);
      setLatency(0);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Listen to connection changes if supported
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      conn.addEventListener("change", checkSpeed);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (conn) {
        conn.removeEventListener("change", checkSpeed);
      }
    };
  }, []);

  // UI styling depending on state
  const statusStyles = {
    fast: {
      bg: "#ecfdf5",
      border: "#a7f3d0",
      text: "#065f46",
      barColor: "#10b981",
      label: "Koneksi Cepat (Stabil)",
      activeBars: 4,
    },
    medium: {
      bg: "#fffbeb",
      border: "#fde68a",
      text: "#92400e",
      barColor: "#f59e0b",
      label: "Koneksi Sedang",
      activeBars: 3,
    },
    slow: {
      bg: "#fef2f2",
      border: "#fecaca",
      text: "#991b1b",
      barColor: "#ef4444",
      label: "Koneksi Lambat (Laggy)",
      activeBars: 1,
    },
    offline: {
      bg: "#f3f4f6",
      border: "#e5e7eb",
      text: "#374151",
      barColor: "#9ca3af",
      label: "Terputus (Offline)",
      activeBars: 0,
    },
    loading: {
      bg: "#f8fafc",
      border: "#e2e8f0",
      text: "#64748b",
      barColor: "#cbd5e1",
      label: "Mengukur Kecepatan...",
      activeBars: 0,
    },
  };

  const currentStyle = statusStyles[status];

  // Tooltip content
  const tooltipContent = (
    <div style={{ fontSize: "12px", padding: "4px", color: "white" }}>
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>Status Jaringan</div>
      <div>Kondisi: <strong>{currentStyle.label}</strong></div>
      {status !== "offline" && status !== "loading" && (
        <>
          <div>Kecepatan: <strong>{speed ? speed.toFixed(1) : "-"} Mbps</strong></div>
          <div>Latency (Ping): <strong>{latency ? `${latency} ms` : "-"}</strong></div>
        </>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          backgroundColor: currentStyle.bg,
          border: `1.5px solid ${currentStyle.border}`,
          borderRadius: "20px",
          padding: "6px 14px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          userSelect: "none",
          height: "36px",
          lineHeight: "1",
        }}
      >
        {/* Signal Bars */}
        <div style={{ display: "flex", alignItems: "flex-end", height: "16px", gap: "2.2px" }}>
          {[4, 8, 12, 16].map((barHeight, index) => {
            const isActive = index < currentStyle.activeBars;
            return (
              <div
                key={index}
                style={{
                  width: "3.2px",
                  height: `${barHeight}px`,
                  backgroundColor: isActive ? currentStyle.barColor : "#e5e7eb",
                  borderRadius: "1px",
                  transition: "background-color 0.3s ease",
                }}
              />
            );
          })}
        </div>

        {/* Info Text */}
        <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
          {status === "loading" ? (
            <span style={{ fontSize: "13px", fontWeight: "600", color: currentStyle.text }}>
              Checking...
            </span>
          ) : status === "offline" ? (
            <span style={{ fontSize: "13px", fontWeight: "700", color: currentStyle.text }}>
              Offline
            </span>
          ) : (
            <span style={{ fontSize: "13px", fontWeight: "700", color: currentStyle.text, whiteSpace: "nowrap" }}>
              {latency !== null ? `${latency} ms` : "- ms"}
            </span>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

export default InternetSpeedDetector;
